import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FactoryDetailsTab = () => {
  const [selectedFactory, setSelectedFactory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data for factories
  const factories = [
    {
      id: 1,
      name: 'Shree Ganesh Sugar Mills',
      location: 'Kolhapur, Maharashtra',
      established: 1995,
      capacity: '5000 TCD',
      contact: {
        phone: '+91 231 2653210',
        email: 'info@sgsm.com',
        address: 'Kagal Industrial Area, Kolhapur - 416234'
      },
      grades: ['S-30', 'S-31', 'M-30'],
      certifications: ['ISO 9001:2015', 'FSSAI', 'HACCP'],
      facilities: ['Cogeneration Plant', 'Distillery', 'Ethanol Production'],
      currentProduction: 4500,
      annualCapacity: 150000,
      qualityStandards: 'BIS, FSSAI, Export Quality',
      rating: 4.8,
      deliveryTime: '2-3 days',
      paymentTerms: '50% Advance, 50% on delivery'
    },
    {
      id: 2,
      name: 'Maharashtra Rajya Sahakari Sugar Factory',
      location: 'Sangli, Maharashtra',
      established: 1988,
      capacity: '3500 TCD',
      contact: {
        phone: '+91 233 2341567',
        email: 'contact@mrssf.com',
        address: 'Miraj Road, Sangli - 416410'
      },
      grades: ['M-30', 'L-30'],
      certifications: ['ISO 9001:2015', 'FSSAI'],
      facilities: ['Cogeneration Plant', 'Molasses Storage'],
      currentProduction: 3200,
      annualCapacity: 120000,
      qualityStandards: 'BIS, FSSAI',
      rating: 4.6,
      deliveryTime: '3-4 days',
      paymentTerms: '40% Advance, 60% on delivery'
    },
    {
      id: 3,
      name: 'Vasantdada Sugar Institute',
      location: 'Pune, Maharashtra',
      established: 1975,
      capacity: '2000 TCD',
      contact: {
        phone: '+91 20 26902451',
        email: 'admin@vsi.com',
        address: 'Manjri, Pune - 412307'
      },
      grades: ['L-30', 'M-30'],
      certifications: ['ISO 9001:2015', 'FSSAI', 'Organic Certified'],
      facilities: ['Research Lab', 'Quality Testing'],
      currentProduction: 1800,
      annualCapacity: 80000,
      qualityStandards: 'BIS, FSSAI, Organic',
      rating: 4.7,
      deliveryTime: '2-3 days',
      paymentTerms: '50% Advance, 50% on delivery'
    },
    {
      id: 4,
      name: 'Dalmia Sugar Mills',
      location: 'Nigoha, Uttar Pradesh',
      established: 1993,
      capacity: '7500 TCD',
      contact: {
        phone: '+91 581 2245678',
        email: 'sales@dalmiasugar.com',
        address: 'Nigoha, Shahjahanpur - 242407'
      },
      grades: ['S-30', 'S-31', 'M-30', 'L-30'],
      certifications: ['ISO 9001:2015', 'ISO 14001', 'FSSAI', 'HACCP'],
      facilities: ['Cogeneration Plant', 'Distillery', 'Ethanol Production', 'Warehousing'],
      currentProduction: 7000,
      annualCapacity: 250000,
      qualityStandards: 'BIS, FSSAI, Export Quality, EU Standards',
      rating: 4.9,
      deliveryTime: '1-2 days',
      paymentTerms: '30% Advance, 70% on delivery'
    },
    {
      id: 5,
      name: 'Bannari Amman Sugars Ltd',
      location: 'Erode, Tamil Nadu',
      established: 1983,
      capacity: '4500 TCD',
      contact: {
        phone: '+91 4294 268421',
        email: 'info@bannariamman.com',
        address: 'Sathyamangalam, Erode - 638401'
      },
      grades: ['M-30', 'S-30'],
      certifications: ['ISO 9001:2015', 'FSSAI', 'HACCP', 'ISO 22000'],
      facilities: ['Cogeneration Plant', 'Distillery', 'Bio-fertilizer Unit'],
      currentProduction: 4200,
      annualCapacity: 180000,
      qualityStandards: 'BIS, FSSAI, Export Quality',
      rating: 4.7,
      deliveryTime: '2-4 days',
      paymentTerms: '40% Advance, 60% on delivery'
    }
  ];

  const filteredFactories = factories?.filter(factory =>
    factory?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    factory?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Search and Summary */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Factory Network</h3>
            <p className="text-sm text-muted-foreground">
              Partnered with {factories?.length} premium sugar mills across India
            </p>
          </div>
          <div className="md:w-1/3">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search factories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00] text-foreground"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Factory" size={20} className="text-blue-600" />
              <span className="text-sm text-muted-foreground">Total Factories</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{factories?.length}</p>
          </div>
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="TrendingUp" size={20} className="text-green-600" />
              <span className="text-sm text-muted-foreground">Total Capacity</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {factories?.reduce((sum, f) => sum + f?.annualCapacity, 0)?.toLocaleString()} MT
            </p>
          </div>
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Award" size={20} className="text-orange-600" />
              <span className="text-sm text-muted-foreground">Avg Rating</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {(factories?.reduce((sum, f) => sum + f?.rating, 0) / factories?.length)?.toFixed(1)}
            </p>
          </div>
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Package" size={20} className="text-purple-600" />
              <span className="text-sm text-muted-foreground">Grade Types</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {[...new Set(factories.flatMap(f => f.grades))]?.length}
            </p>
          </div>
        </div>
      </div>
      {/* Factory Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredFactories?.map((factory) => (
          <div key={factory?.id} className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-1">{factory?.name}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="MapPin" size={16} />
                  <span>{factory?.location}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Star" size={16} className="text-yellow-500 fill-yellow-500" />
                <span className="font-semibold text-foreground">{factory?.rating}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Established</p>
                <p className="font-medium text-foreground">{factory?.established}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Capacity</p>
                <p className="font-medium text-foreground">{factory?.capacity}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Annual Capacity</p>
                <p className="font-medium text-foreground">{factory?.annualCapacity?.toLocaleString()} MT</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Delivery Time</p>
                <p className="font-medium text-foreground">{factory?.deliveryTime}</p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-2">Available Grades</p>
              <div className="flex flex-wrap gap-2">
                {factory?.grades?.map((grade, idx) => (
                  <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    {grade}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-2">Certifications</p>
              <div className="flex flex-wrap gap-2">
                {factory?.certifications?.map((cert, idx) => (
                  <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                    {cert}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-2">Facilities</p>
              <div className="flex flex-wrap gap-2">
                {factory?.facilities?.map((facility, idx) => (
                  <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                    {facility}
                  </span>
                ))}
              </div>
            </div>

            <div className="border-t border-border pt-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <Icon name="Phone" size={16} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p className="text-foreground">{factory?.contact?.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="Mail" size={16} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-foreground break-all">{factory?.contact?.email}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                className="flex-1 bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
                onClick={() => setSelectedFactory(factory)}
              >
                <Icon name="Eye" size={18} />
                <span>View Details</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => {}}
              >
                <Icon name="Phone" size={18} />
              </Button>
            </div>
          </div>
        ))}
      </div>
      {filteredFactories?.length === 0 && (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <Icon name="Search" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Factories Found</h3>
          <p className="text-muted-foreground">Try adjusting your search criteria</p>
        </div>
      )}
    </div>
  );
};

export default FactoryDetailsTab;